import { isAppLocale, type AppLocale } from '@floorfile/i18n'

/**
 * The cold-boot-offline locale seed (localization plan §3.6).
 *
 * On a cold PWA start with no network, `GET /settings/navigation` fails and
 * `CompanyConfigProvider` falls back to its `defaults`, whose `effectiveLocale`
 * is `'en'` — so a French plant's tablet, opened in a dead zone, would come up
 * in English until it found signal. The resolved locale is therefore mirrored
 * here on every successful settings load and read back before the first fetch
 * resolves.
 *
 * **Deliberately unscoped**, unlike every other preference (audit FF-06). This
 * is the same exception `requireOfflineEncryption` already takes and for the
 * same reason: it is read BEFORE any user context exists, so there is no
 * `(companyId, userId)` to key it by. The exposure is one language hint on a
 * shared tablet for the few hundred milliseconds until the real settings
 * arrive — and the value is a company-wide default far more often than a
 * personal override. `localStorage`, not `sessionStorage`, precisely because it
 * has to survive the tab closing; that is the case it exists for.
 *
 * Everything degrades quietly: private mode and quota failures just mean the
 * app starts in English, which is exactly where it started before this existed.
 */
const KEY = 'ff.locale'
/**
 * FF-AUDIT-088: the company time zone rides beside the locale for the same
 * cold-offline reason — without it a reload in a dead zone formatted every due
 * time in UTC, which reads as a different day. A time zone is a display
 * preference, never a permission, so seeding it carries the locale's risk and
 * no more.
 */
const TZ_KEY = 'ff.timezone'

export function readLocaleSeed(): AppLocale | undefined {
  try {
    const raw = localStorage.getItem(KEY)
    return isAppLocale(raw) ? raw : undefined
  } catch {
    return undefined
  }
}

export function writeLocaleSeed(locale: AppLocale): void {
  try {
    localStorage.setItem(KEY, locale)
  } catch {
    // Session-only is the correct failure for a hint.
  }
}

export function readTimezoneSeed(): string | undefined {
  try {
    const raw = localStorage.getItem(TZ_KEY)
    return raw && raw.length <= 64 ? raw : undefined
  } catch {
    return undefined
  }
}

export function writeTimezoneSeed(timezone: string): void {
  try {
    localStorage.setItem(TZ_KEY, timezone)
  } catch {
    // Same degradation as the locale: UTC until the settings arrive.
  }
}
